import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
// eslint-disable-next-line no-restricted-imports
import type { useQuery as apolloUseQuery } from '@apollo/client/react';
import { useCallback, useRef } from 'react';
import { useDeepCompareEffect } from 'react-use';

import type { CompleteResult as CompleteQueryResult } from './useQuery.ts';
import { useQuery } from './useQuery.ts';

// fetchMore no longer resolves to ApolloQueryResult in v4.
type FetchMoreResult<TData, TVariables extends OperationVariables> = Awaited<
  ReturnType<apolloUseQuery.Result<TData, TVariables>['fetchMore']>
>;

export type LoadMore<D, V> = (
  reload: boolean,
  variables: V,
  forceConnection?: string
) => Promise<D>;

type PaginatedQueryOptions = (
  | { connection: string; connections?: never }
  | { connections: string[]; connection?: never }
) & {
  prepend?: boolean;
};

type UsePaginatedQueryOptions<
  TData,
  TVariables extends OperationVariables,
> = apolloUseQuery.Options<TData, TVariables> & PaginatedQueryOptions;

type Connection = { __typename?: unknown; pageInfo?: unknown };
type EdgeConnection = Connection & { edges: unknown[] };
type NodeConnection = Connection & { nodes: unknown[] };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isEdgeConnection = (value: unknown): value is EdgeConnection =>
  isRecord(value) && Array.isArray(value.edges);

const isNodeConnection = (value: unknown): value is NodeConnection =>
  isRecord(value) && Array.isArray(value.nodes);

function mergeEdges({
  previous,
  current,
  reload,
  prepend,
}: {
  previous: EdgeConnection | undefined;
  current: EdgeConnection;
  reload: boolean;
  prepend: boolean;
}) {
  const newEdges = current.edges;
  const { pageInfo } = current;
  let edges = [];

  if (reload) {
    edges = [...newEdges];
  } else if (previous) {
    edges = prepend
      ? [...newEdges, ...previous.edges]
      : [...previous.edges, ...newEdges];
  } else {
    edges = newEdges;
  }
  return { ...previous, ...current, edges, pageInfo };
}

function mergeNodes({
  previous,
  current,
  reload,
  prepend,
}: {
  previous: NodeConnection | undefined;
  current: NodeConnection;
  reload: boolean;
  prepend: boolean;
}) {
  const newNodes = current.nodes;
  const { pageInfo } = current;
  let nodes = [];

  if (reload) {
    nodes = [...newNodes];
  } else if (previous) {
    nodes = prepend
      ? [...newNodes, ...previous.nodes]
      : [...previous.nodes, ...newNodes];
  } else {
    nodes = newNodes;
  }
  return { ...previous, ...current, nodes, pageInfo };
}

function mergeResults({
  previous,
  current,
  reload,
  connections,
  prepend,
}: {
  previous: unknown;
  current: unknown;
  reload: boolean;
  connections: string[];
  prepend: boolean;
}): unknown {
  if (previous === null || current === null) {
    return current;
  }
  if (Array.isArray(current) || Array.isArray(previous)) {
    return current;
  }
  if (!isRecord(current)) {
    return current;
  }

  const isListedConnection =
    typeof current.__typename === 'string' &&
    connections.includes(current.__typename);

  if (current.edges) {
    if (isListedConnection && isEdgeConnection(current)) {
      return mergeEdges({
        previous: isEdgeConnection(previous) ? previous : undefined,
        current,
        reload,
        prepend,
      });
    }
    return current;
  }
  if (current.nodes) {
    if (isListedConnection && isNodeConnection(current)) {
      return mergeNodes({
        previous: isNodeConnection(previous) ? previous : undefined,
        current,
        reload,
        prepend,
      });
    }
    return current;
  }

  const previousRecord = isRecord(previous) ? previous : undefined;
  const result: Record<string, unknown> = {};

  Object.keys(current).forEach(key => {
    result[key] = mergeResults({
      previous: previousRecord?.[key],
      current: current[key],
      reload,
      connections,
      prepend,
    });
  });

  return { ...previousRecord, ...result };
}

interface WithRelayPagination<D, V extends OperationVariables> {
  loadMore: LoadMore<FetchMoreResult<D, V>, Partial<V>>;
}

export const useLoadMore = <TData, TVariables extends OperationVariables>({
  fetchMore,
  options,
}: {
  fetchMore: apolloUseQuery.Result<TData, TVariables>['fetchMore'];
  options: Nullable<UsePaginatedQueryOptions<TData, TVariables>>;
}) => {
  const { connection, connections, prepend, ...rest } = options || {};

  // Add a ref to keep track of the latest version
  const versionRef = useRef(0);

  useDeepCompareEffect(() => {
    versionRef.current += 1;
  }, [rest]);

  const loadMore = useCallback(
    async (
      reload: boolean,
      loadMoreVariables: Partial<TVariables>,
      forceConnection?: string
    ) => {
      const currentVersion = versionRef.current;

      return fetchMore({
        variables: loadMoreVariables,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (currentVersion !== versionRef.current) {
            // cache.updateQuery skips the write on null, but fetchMore's updateQuery type omits null.
            return null as unknown as typeof previousResult;
          }

          const merged = mergeResults({
            previous: previousResult,
            current: fetchMoreResult,
            reload,
            connections: forceConnection
              ? [forceConnection]
              : connections || [connection].filter(Boolean),
            prepend: Boolean(prepend),
          });
          return merged as typeof previousResult;
        },
      });
    },
    [fetchMore, connections, connection, prepend]
  );
  return loadMore;
};

export const usePaginatedQuery = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: TypedDocumentNode<TData, TVariables>,
  options: UsePaginatedQueryOptions<TData, NoInfer<TVariables>>
): CompleteQueryResult<TData, TVariables> &
  WithRelayPagination<TData, TVariables> => {
  const { connection, connections, prepend, ...rest } = options;
  const data = useQuery(
    query,
    rest as Parameters<typeof useQuery<TData, TVariables>>[1]
  );
  const { fetchMore } = data;

  const loadMore = useLoadMore({
    fetchMore,
    options,
  });

  return {
    ...data,
    loadMore,
  };
};
